/**
 * <swiper-init> — Custom Element that auto-initializes a native Swiper 12
 * instance on connectedCallback. Reads config from `data-swiper-settings`
 * JSON either on `this` (element itself is the Swiper root) or on the first
 * `[data-swiper-settings]` child. Optional thumb swiper via
 * `data-swiper-thumb="<selector>"` + `data-swiper-thumb-settings='<json>'`
 * on the thumb element.
 *
 * Usage in liquid:
 *   <swiper-init class="swiper" data-swiper-settings='{{ swiper_options }}'>
 *     <div class="swiper-wrapper">...</div>
 *   </swiper-init>
 *
 * Public API:
 *   element.swiper         — native Swiper 12 instance (set by Swiper itself
 *                            on `mount()`; read after `swiper:initialized`)
 *   element.thumbSwiper    — native thumb Swiper instance (if any)
 *   element.initialize()   — initializes once and resolves when loading finishes
 *
 * Dispatched events (bubbles):
 *   swiper:initialized  → { swiper, thumbs }
 *   swiper:destroyed
 *   swiper:slide-changed → { currentIndex, element }
 *
 * Loaded globally from `snippets/theme-scripts.liquid` so every page has the
 * Custom Element defined.
 *
 * Swiper itself is dynamically imported only when a connected
 * `<swiper-init>` element needs to initialize.
 */

let swiperModulePromise = null;

function loadSwiper() {
  swiperModulePromise ??= import('@theme/swiper')
    .then(({ default: Swiper }) => Swiper)
    .catch((error) => {
      swiperModulePromise = null;
      throw error;
    });

  return swiperModulePromise;
}

class SwiperInit extends HTMLElement {
  thumbSwiper = null;
  #rootEl = null;
  #thumbEl = null;
  #initialized = false;
  #initializePromise = null;

  connectedCallback() {
    if (this.#initialized) return;
    if (this.hasAttribute('data-defer-init')) return;

    this.initialize();
  }

  disconnectedCallback() {
    if (!this.#initialized) return;
    this.#destroy();
    this.#initialized = false;
  }

  /** Call after dynamic slides are injected (e.g. cart-upsell-full lazy load). */
  initialize() {
    if (this.#initialized) return Promise.resolve();
    if (this.#initializePromise) return this.#initializePromise;

    const initializePromise = this.#loadAndInit();
    this.#initializePromise = initializePromise;
    initializePromise.finally(() => {
      if (this.#initializePromise === initializePromise) {
        this.#initializePromise = null;
      }
    });

    return initializePromise;
  }

  async #loadAndInit() {
    try {
      this.#prepare();
    } catch (error) {
      console.error('<swiper-init>: invalid setup', error);
      return;
    }

    if (!this.#rootEl) return;

    let Swiper;
    try {
      Swiper = await loadSwiper();
    } catch (error) {
      console.error('<swiper-init>: failed to load Swiper', error);
      return;
    }

    if (!this.isConnected || this.#initialized) return;

    try {
      this.#initialized = this.#init(Swiper);
    } catch (error) {
      console.error('<swiper-init>: failed to initialize Swiper', error);
    }
  }

  #prepare() {
    this.#rootEl = this.hasAttribute('data-swiper-settings')
      ? this
      : this.querySelector('[data-swiper-settings]');

    if (!this.#rootEl) {
      console.warn('<swiper-init>: no [data-swiper-settings] found', this);
      return;
    }

    const thumbSelector = this.getAttribute('data-swiper-thumb');
    if (thumbSelector) {
      this.#thumbEl = document.querySelector(thumbSelector);
    }
  }

  #init(Swiper) {
    if (!this.#rootEl) return false;

    let config;
    try {
      config = JSON.parse(this.#rootEl.getAttribute('data-swiper-settings'));
    } catch (e) {
      console.error('<swiper-init>: invalid data-swiper-settings JSON', e);
      return false;
    }

    if (this.#thumbEl) {
      const thumbConfig = JSON.parse(
        this.#thumbEl.getAttribute('data-swiper-thumb-settings') || '{}'
      );
      this.thumbSwiper = new Swiper(this.#thumbEl, thumbConfig);
      config.thumbs = { ...(config.thumbs || {}), swiper: this.thumbSwiper };
    }

    // Swiper sets `this.#rootEl.swiper = <instance>` internally on mount().
    // When #rootEl === this (the Custom Element), that means `this.swiper`
    // becomes the native Swiper instance automatically.
    const swiper = new Swiper(this.#rootEl, config);

    swiper.on('slideChange', () => {
      this.dispatchEvent(
        new CustomEvent('swiper:slide-changed', {
          bubbles: true,
          detail: { currentIndex: swiper.activeIndex, element: swiper.el },
        })
      );
    });

    this.#bindEdgeFade(swiper);

    this.dispatchEvent(
      new CustomEvent('swiper:initialized', {
        bubbles: true,
        detail: { swiper, thumbs: this.thumbSwiper },
      })
    );

    return true;
  }

  #bindEdgeFade(swiper) {
    const rootEl = this.#rootEl;
    if (!rootEl?.classList.contains('swiper--edge-fade')) {
      return;
    }

    const update = () => {
      rootEl.classList.toggle('swiper--at-start', swiper.isBeginning);
      rootEl.classList.toggle('swiper--at-end', swiper.isEnd);
    };

    swiper.on('slideChange', update);
    swiper.on('resize', update);
    swiper.on('reachBeginning', update);
    swiper.on('reachEnd', update);
    swiper.on('fromEdge', update);
    update();
  }

  #destroy() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
    }
    if (this.thumbSwiper) {
      this.thumbSwiper.destroy(true, true);
      this.thumbSwiper = null;
    }
    this.dispatchEvent(new CustomEvent('swiper:destroyed', { bubbles: true }));
  }
}

if (!customElements.get('swiper-init')) {
  customElements.define('swiper-init', SwiperInit);
}
